import ReactPixel from "react-facebook-pixel";
import { SubscriptionPlan, UserInfo } from "../../components/signup/SignUpApi";
import { CheckoutLine, StoreCheckout } from "../../components/checkout/Checkout.types";
import { isTrackingAllowed } from "./consent";
import { TrackedProduct } from "./types";

const pixelIdFromConfig = process.env.REACT_APP_FACEBOOK_PIXEL_ID || undefined;

/** Meta's `content_type` for a single item, as opposed to `product_group`. */
const CONTENT_TYPE_PRODUCT = "product";

export const getStoreCheckoutContents = (checkout: StoreCheckout): any => {
  const contents: any = [];
  const content_ids: string[] = [];
  checkout?.lines.forEach((line: CheckoutLine) => {
    const { sku, product, pricing } = line?.variant ?? {};
    const { id, name } = product ?? {};
    const { price } = pricing ?? {};
    if (id) {
      content_ids.push(id);
      contents.push({ id, name, sku, price });
    }
  });
  return { contents, content_ids };
};

export const buildPaymentDataFromStoreCheckout = (checkout: StoreCheckout): any => {
  const { content_ids, contents } = getStoreCheckoutContents(checkout);
  const { amount, currency } = checkout?.totalPrice?.gross ?? {};
  return {
    value: amount?.toFixed(2),
    currency: currency,
    content_type: CONTENT_TYPE_PRODUCT,
    content_ids,
    contents,
  };
};

export const buildProductData = (product: TrackedProduct): any => {
  const { id, name, price, currency, quantity } = product;
  return {
    content_type: CONTENT_TYPE_PRODUCT,
    content_ids: id ? [id] : [],
    contents: [
      {
        id,
        name,
        quantity: quantity ?? 1,
        item_price: price,
      },
    ],
    /* `value` is what the whole event is worth, so it multiplies out. */
    value: price != null ? (price * (quantity ?? 1)).toFixed(2) : undefined,
    currency,
  };
};

class FacebookPixelAPI {
  /** Set by the cookie banner. Consent can also arrive as a stored cookie. */
  private _isAllowed = false;
  private _isLoaded = false;

  /**
   * Neither app asks for consent, so this is true for every visitor — see
   * `consent.ts` for the decision and the one switch that reverses it.
   *
   * This used to read the consent cookie directly and call `revokeConsent()`
   * whenever it was unset, which — once the banner stopped rendering — meant
   * every visitor. The pixel loaded and then silenced itself, so nothing was
   * ever reported. Routing through `consent.ts` is what puts it back on, and
   * puts Facebook on the same switch as TikTok and Google Ads.
   */
  private get isAllowed(): boolean {
    return isTrackingAllowed(this._isAllowed);
  }

  /**
   * Loads the pixel. Safe to call repeatedly and from anywhere — the SDK only
   * injects its script once.
   *
   * `userInfo` is optional so the pixel can also cover anonymous browsing,
   * which is where the top of the funnel (`ViewContent`, `AddToCart`) happens.
   * Unlike TikTok's SDK, this one re-runs `fbq('init', …)` on every call, so
   * passing `userInfo` later is how advanced matching gets attached.
   */
  init(userInfo?: UserInfo) {
    /* Consent is checked here rather than only at the call sites, because
       loading the SDK is itself the disclosure: with `userInfo` it hands Meta
       the customer's details. */
    if (!pixelIdFromConfig || !this.isAllowed) {
      return;
    }
    const advancedMatching: ReactPixel.AdvancedMatching | undefined =
      userInfo && userInfo.email
        ? ({
            em: userInfo.email,
            ct: userInfo.city ?? "",
            country: "US",
            db: userInfo.dateBirth ?? "",
            fn: userInfo.firstName ?? "",
            ge: "",
            ln: userInfo.lastName ?? "",
            ph: userInfo.phoneNumber ?? "",
            st: "",
            zp: userInfo.postCode ?? "",
            extern_id: userInfo.membershipNumber ?? undefined,
            external_id: userInfo.membershipNumber ?? undefined,
          } as any)
        : undefined;
    const options: ReactPixel.Options = {
      autoConfig: true,
      debug: false,
    };
    ReactPixel.init(pixelIdFromConfig, advancedMatching, options);
    this._isLoaded = true;
  }

  allowTracking() {
    this._isAllowed = true;
  }

  /**
   * A route change in a single-page app is a page view, and this SDK sends
   * none of its own — not even at load, unlike the snippet Meta hands out.
   */
  pageView() {
    if (!this.canTrack()) return;
    ReactPixel.pageView();
  }

  trackViewContent(product: TrackedProduct) {
    if (!this.canTrack()) return;
    ReactPixel.track("ViewContent", buildProductData(product));
  }

  trackAddToCart(product: TrackedProduct) {
    if (!this.canTrack()) return;
    ReactPixel.track("AddToCart", buildProductData(product));
  }

  trackInitiateCheckout(checkout: StoreCheckout) {
    if (!this.canTrack()) return;
    ReactPixel.track("InitiateCheckout", buildPaymentDataFromStoreCheckout(checkout));
  }

  trackPurchase(checkout: StoreCheckout, userInfo?: UserInfo) {
    if (!this.canTrack(userInfo)) return;

    const data = buildPaymentDataFromStoreCheckout(checkout);
    ReactPixel.track("Purchase", data);
    ReactPixel.trackCustom("Product Purchase", data);
  }

  trackSubscribe(plan: SubscriptionPlan, userInfo?: UserInfo) {
    if (!this.canTrack(userInfo)) return;

    const { currency, amount, subscriptionPlanId: id, title } = plan ?? {};
    const amountWithDecimals = amount?.toFixed(2);
    const data = {
      currency,
      value: amountWithDecimals,
      content_type: CONTENT_TYPE_PRODUCT,
      content_ids: [id],
      contents: [
        {
          id: id,
          quantity: 1,
        },
      ],
      plan: `${title}: ${amountWithDecimals}/month`,
    };
    ReactPixel.track("Subscribe", data);
    /* Both, deliberately, and long-standing. A signup therefore reaches Meta
       as a Subscribe AND a Purchase: whoever sets a campaign up has to pick
       ONE as the conversion, or every signup counts twice. */
    ReactPixel.track("Purchase", data);
    ReactPixel.trackCustom("Subscription Purchased", data);
  }

  /** A signup reaching its end — the acquisition conversion, not a sale. */
  trackCompleteRegistration(userInfo?: UserInfo) {
    if (!this.canTrack(userInfo)) return;
    ReactPixel.track("CompleteRegistration", {});
  }

  trackLogin(userInfo?: UserInfo) {
    if (!this.canTrack(userInfo)) return;
    ReactPixel.trackCustom("Login", {});
  }

  /**
   * Nothing is sent without consent, and nothing can be sent before the SDK is
   * on the page.
   *
   * @param userInfo when present, re-runs `init` so the customer's details are
   *   attached to this event and the ones after it.
   */
  private canTrack(userInfo?: UserInfo): boolean {
    if (!this.isAllowed) {
      return false;
    }
    /* An event can be the first thing to happen after consent arrives, so the
       load is attempted rather than assumed. `init` is a no-op when no pixel
       is configured. */
    if (userInfo || !this._isLoaded) {
      this.init(userInfo);
    }
    /* Deliberately NOT also requiring the SDK to be present: every method on
       it checks that itself and quietly does nothing, whereas gating here
       would stop the wrapper calling it at all wherever no pixel id is set —
       which includes every test run, since the CI test job configures no
       environment at all. */
    return true;
  }
}

export default new FacebookPixelAPI();
